import { useEffect, useRef, useState } from 'react';
import { useLocation, type Location } from 'react-router-dom';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';
import { useIntegrationsContext } from 'src/providers/IntegrationsProvider/Integrations.context';
import { useWhatsappApiContext } from 'src/pages/WhatsappApi/WhatsappApi.context';
import { eWhatsappApiWindow } from 'src/pages/WhatsappApi/WhatsappApi.enum';
import { resolveWapiConnectionState } from 'src/pages/WhatsappApi/hooks/useWapiNotConnected';

export type tWapiEntryPoint = 'sidebar' | 'direct_url' | 'other';

/** The router state the Sidebar's WhatsApp API link carries (INT-1633). */
export const WAPI_SIDEBAR_ENTRY_STATE = { wapiEntryPoint: 'sidebar' } as const;

/**
 * Where the module was opened from. The router state is external input, so it
 * is checked at runtime rather than cast. React Router gives the first location
 * of the session the key "default": the module was the page the browser loaded
 * (a typed or bookmarked URL, a reload).
 */
export function resolveWapiEntryPoint(location: Location): tWapiEntryPoint {
  const state: unknown = location.state;
  if (
    typeof state === 'object' &&
    state !== null &&
    (state as Record<string, unknown>).wapiEntryPoint === 'sidebar'
  ) {
    return 'sidebar';
  }
  return location.key === 'default' ? 'direct_url' : 'other';
}

type tWapiSection = 'general' | 'templates' | 'campaigns' | 'credits' | 'flows';

// The template wizard is part of Plantillas: opening it, or coming back from it
// to the list, is not a new visit to the section.
const SECTION_BY_WINDOW: Record<eWhatsappApiWindow, tWapiSection> = {
  [eWhatsappApiWindow.GENERAL]: 'general',
  [eWhatsappApiWindow.VIEW_TEMPLATES]: 'templates',
  [eWhatsappApiWindow.CREATE_TEMPLATE]: 'templates',
  [eWhatsappApiWindow.EDIT_TEMPLATE]: 'templates',
  [eWhatsappApiWindow.CAMPAIGNS]: 'campaigns',
  [eWhatsappApiWindow.CREDITS]: 'credits',
  [eWhatsappApiWindow.BALANCE]: 'credits',
  [eWhatsappApiWindow.FLOWS]: 'flows',
};

type tWapiEventName = (typeof WAPI_EVENTS)[keyof typeof WAPI_EVENTS];

// Only the sections INT-1632 measures depth with.
const TAB_VIEW_EVENT: Partial<Record<tWapiSection, tWapiEventName>> = {
  templates: WAPI_EVENTS.TEMPLATES_TAB_VIEW,
  campaigns: WAPI_EVENTS.CAMPAIGNS_TAB_VIEW,
  flows: WAPI_EVENTS.FLOWS_TAB_VIEW,
};

/**
 * INT-1633 — the WhatsApp API module's exposure and depth events: one
 * `general.page.view` per mount of the page, and one `*.tab.view` each time
 * the user moves into Plantillas, Flows or Campañas while it is mounted.
 *
 * Mount it once, on the page: it is a side effect, and a second consumer
 * would double every event. Both wait for the integrations to hydrate, since
 * the state defaults to NO_ACCOUNT until then and would report a connected
 * workspace as `not_connected`.
 */
export function useWapiModuleViewAnalytics() {
  const { trackWapiEvent } = useWapiAnalytics();
  const { whatsappApiWindow } = useWhatsappApiContext();
  const { whatsappBusinessIntegration, isHydrated } = useIntegrationsContext();
  const location = useLocation();
  const [entryPoint] = useState(() => resolveWapiEntryPoint(location));
  const hasSentPageViewRef = useRef(false);
  const lastSectionRef = useRef<tWapiSection | null>(null);

  // trackWapiEvent is rebuilt on every render; the effects must fire on their
  // own triggers only.
  const trackRef = useRef(trackWapiEvent);
  trackRef.current = trackWapiEvent;

  const connectionState = resolveWapiConnectionState(
    whatsappBusinessIntegration.state,
  );

  useEffect(() => {
    if (!isHydrated || hasSentPageViewRef.current) return;
    hasSentPageViewRef.current = true;
    trackRef.current(WAPI_EVENTS.GENERAL_PAGE_VIEW, {
      connection_state: connectionState,
      entry_point: entryPoint,
    });
  }, [isHydrated, connectionState, entryPoint]);

  useEffect(() => {
    if (!isHydrated) return;
    const section = SECTION_BY_WINDOW[whatsappApiWindow];
    if (section === lastSectionRef.current) return;
    lastSectionRef.current = section;
    const eventName = TAB_VIEW_EVENT[section];
    if (eventName) {
      trackRef.current(eventName, { connection_state: connectionState });
    }
    // connectionState is read, not a trigger: the account connecting while
    // the user stays on a tab is not a new visit to it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated, whatsappApiWindow]);
}
